import axios from 'axios';
import { useEffect, useState } from 'react';
import type { CimpressDocumentV2 } from '@mcp-artwork/cimdoc';
import { uploadJsonDocumentAndGetDocumentUrl } from '../../api';

const TRUSTED_DOCUMENT_HOSTS = ['storage.documents.cimpress.io'];

// Resize and Generate reject transient source documents with a 422, so they have to be stored first.
// Only transient documents from the trusted storage host are fetched, since the url can come from the page link.
const isTransientDocumentUrl = (url: string): boolean => {
    try {
        const { protocol, hostname, pathname } = new URL(url);
        return protocol === 'https:' && TRUSTED_DOCUMENT_HOSTS.includes(hostname) && pathname.includes('/transient');
    } catch {
        return false;
    }
};

const storageUrlCache = new Map<string, string>();

interface StoredSource {
    source: string;
    url?: string;
}

interface StorageDocumentUrl {
    /** Url to send to the service. Empty while a transient document is being stored or if storing failed. */
    url: string;
    isPreparing: boolean;
    hasError: boolean;
}

/**
 * Returns a storage url for the given source document.
 * Documents that are already stored are returned as they are, transient ones are saved to storage first.
 */
export const useStorageDocumentUrl = (sourceDocumentUrl: string): StorageDocumentUrl => {
    const [storedSource, setStoredSource] = useState<StoredSource | null>(null);
    const needsStorageUrl = isTransientDocumentUrl(sourceDocumentUrl);

    useEffect(() => {
        if (!needsStorageUrl) {
            return;
        }

        const cachedUrl = storageUrlCache.get(sourceDocumentUrl);
        if (cachedUrl) {
            setStoredSource({ source: sourceDocumentUrl, url: cachedUrl });
            return;
        }

        const controller = new AbortController();
        const { signal } = controller;

        (async () => {
            try {
                // Transient documents are public, so the request is sent without credentials
                const { data: document } = await axios.get<CimpressDocumentV2>(sourceDocumentUrl, { signal });
                const url = await uploadJsonDocumentAndGetDocumentUrl(document, signal, 1);
                storageUrlCache.set(sourceDocumentUrl, url);
                setStoredSource({ source: sourceDocumentUrl, url });
            } catch (error) {
                if (signal.aborted) {
                    return;
                }
                console.error('Failed to store the transient source document:', error);
                setStoredSource({ source: sourceDocumentUrl });
            }
        })();

        return () => {
            controller.abort();
        };
    }, [sourceDocumentUrl, needsStorageUrl]);

    if (!needsStorageUrl) {
        return { url: sourceDocumentUrl, isPreparing: false, hasError: false };
    }

    const storedForUrl = storedSource?.source === sourceDocumentUrl ? storedSource : null;
    return {
        url: storedForUrl?.url ?? '',
        isPreparing: !storedForUrl,
        hasError: !!storedForUrl && !storedForUrl.url,
    };
};
